import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL, getErrorMessage } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { User, Mail, Phone, Lock, Camera, Save, ImagePlus, Shield, Trash2, AlertTriangle, Bell, BellOff, MessageSquare } from 'lucide-react';
import { autoFormatPhone, formatCanadianPhone, displayPhone } from '@/utils/phone';

export default function AccountSettingsPage() {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [profilePicPreview, setProfilePicPreview] = useState(null);
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState({ email_enabled: true, sms_enabled: true });
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileForm({ name: user.name || '', email: user.email || '', phone: displayPhone(user.phone || '') });
      setProfilePicPreview(user.picture ? (user.picture.startsWith('http') ? user.picture : `${BACKEND_URL}/api/uploads/${user.picture}`) : null);
      const prefs = user.notification_preferences || {};
      setNotifPrefs({
        email_enabled: prefs.email_enabled !== false,
        sms_enabled: prefs.sms_enabled !== false,
      });
    }
  }, [user]);

  const handleProfilePicChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setProfilePicFile(file);
    setProfilePicPreview(URL.createObjectURL(file));
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      let updates = { ...profileForm };
      // Normalize phone to E.164
      if (updates.phone) {
        const normalized = formatCanadianPhone(updates.phone);
        if (!normalized) { toast.error('Please enter a valid Canadian phone number'); setSaving(false); return; }
        updates.phone = normalized;
      }
      if (profilePicFile) {
        const formData = new FormData();
        formData.append('file', profilePicFile);
        const imgRes = await api.post('/skill-images/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        updates.picture = imgRes.data.filename;
      }
      await api.put('/auth/profile', updates);
      setProfilePicFile(null);
      await refreshUser();
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update profile'));
    } finally { setSaving(false); }
  };

  const handleChangePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setChangingPw(true);
    try {
      await api.put('/auth/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated!');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to change password'));
    } finally { setChangingPw(false); }
  };

  const handleSaveNotifPrefs = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/auth/profile', { notification_preferences: notifPrefs });
      await refreshUser();
      toast.success('Notification preferences saved!');
    } catch { toast.error('Failed to save preferences'); }
    finally { setSavingPrefs(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto animate-fadeIn" data-testid="account-settings-page">
      <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800 mb-2" style={{ fontFamily: 'Nunito' }}>Account Settings</h1>
      <p className="text-slate-500 mb-8">Manage your profile and security</p>

      {/* Profile Section */}
      <Card className="rounded-3xl border-slate-100 shadow-sm mb-6" data-testid="profile-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
            <User className="w-5 h-5 text-[#22D3EE]" /> Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Avatar */}
          <div className="flex items-center gap-5">
            <div className="relative">
              {profilePicPreview ? (
                <img src={profilePicPreview} alt="Profile" className="w-24 h-24 rounded-2xl object-cover border-2 border-[#22D3EE]/30 shadow-md" />
              ) : (
                <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#ECFEFF] to-[#A5F3FC] flex items-center justify-center">
                  <span className="text-3xl font-extrabold text-[#0E7490]" style={{ fontFamily: 'Nunito' }}>{user.name?.charAt(0) || '?'}</span>
                </div>
              )}
              <label className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#22D3EE] rounded-full flex items-center justify-center cursor-pointer hover:bg-[#06B6D4] transition-colors shadow-md" data-testid="change-avatar-btn">
                <ImagePlus className="w-4 h-4 text-white" />
                <input type="file" accept="image/*" className="hidden" onChange={handleProfilePicChange} />
              </label>
            </div>
            <div>
              <p className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>{user.name}</p>
              <p className="text-sm text-slate-500">{user.email}</p>
              <span className="inline-block mt-1 px-3 py-0.5 rounded-full text-xs font-bold bg-[#ECFEFF] text-[#0E7490] capitalize">{user.role}</span>
            </div>
          </div>

          {/* Form Fields */}
          <div className="space-y-4">
            <div>
              <Label className="font-semibold text-slate-600">Full Name</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input data-testid="settings-name" className="pl-10 rounded-xl" value={profileForm.name} onChange={e => setProfileForm({ ...profileForm, name: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input data-testid="settings-email" className="pl-10 rounded-xl" type="email" value={profileForm.email} onChange={e => setProfileForm({ ...profileForm, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="font-semibold text-slate-600">Phone (Canadian)</Label>
              <div className="relative mt-1">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input data-testid="settings-phone" className="pl-10 rounded-xl" placeholder="(250) 555-1234" value={profileForm.phone} onChange={e => setProfileForm({ ...profileForm, phone: autoFormatPhone(e.target.value) })} maxLength={14} />
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Canadian number for SMS notifications</p>
            </div>
          </div>

          <Button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={saving} className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-white font-bold gap-2">
            <Save className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Profile'}
          </Button>
        </CardContent>
      </Card>

      {/* Password Section */}
      <Card className="rounded-3xl border-slate-100 shadow-sm" data-testid="password-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
            <Shield className="w-5 h-5 text-[#C4F017]" /> Password
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="font-semibold text-slate-600">Current Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input data-testid="current-password" className="pl-10 rounded-xl" type="password" placeholder="Enter current password" value={passwordForm.current_password} onChange={e => setPasswordForm({ ...passwordForm, current_password: e.target.value })} />
            </div>
            <p className="text-xs text-slate-400 mt-1">Leave blank if you signed up with Google and haven't set a password yet</p>
          </div>
          <div>
            <Label className="font-semibold text-slate-600">New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input data-testid="new-password" className="pl-10 rounded-xl" type="password" placeholder="At least 6 characters" value={passwordForm.new_password} onChange={e => setPasswordForm({ ...passwordForm, new_password: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="font-semibold text-slate-600">Confirm New Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              <Input data-testid="confirm-password" className="pl-10 rounded-xl" type="password" placeholder="Repeat new password" value={passwordForm.confirm_password} onChange={e => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} />
            </div>
          </div>
          <Button data-testid="change-password-btn" onClick={handleChangePassword} disabled={changingPw} variant="outline" className="rounded-full border-2 border-slate-200 font-bold gap-2">
            <Lock className="w-4 h-4" /> {changingPw ? 'Updating...' : 'Change Password'}
          </Button>
        </CardContent>
      </Card>

      {/* Notification Preferences */}
      <Card className="rounded-3xl border-slate-100 shadow-sm mt-6" data-testid="notification-preferences-section">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito' }}>
            <Bell className="w-5 h-5 text-[#22D3EE]" /> Notification Preferences
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-500">Choose how you'd like to receive notifications about your child's progress.</p>

          <div className="space-y-3">
            <div
              className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-colors cursor-pointer ${notifPrefs.email_enabled ? 'border-[#22D3EE] bg-[#ECFEFF]' : 'border-slate-200 bg-slate-50'}`}
              onClick={() => setNotifPrefs(p => ({ ...p, email_enabled: !p.email_enabled }))}
              data-testid="toggle-email-notifications"
            >
              <div className="flex items-center gap-3">
                <Mail className={`w-5 h-5 ${notifPrefs.email_enabled ? 'text-[#0E7490]' : 'text-slate-400'}`} />
                <div>
                  <p className={`font-bold text-sm ${notifPrefs.email_enabled ? 'text-[#0E7490]' : 'text-slate-500'}`}>Email Notifications</p>
                  <p className="text-xs text-slate-400">Videos, reports, weekly digests</p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full relative transition-colors ${notifPrefs.email_enabled ? 'bg-[#22D3EE]' : 'bg-slate-300'}`}>
                <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${notifPrefs.email_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>

            <div
              className={`flex items-center justify-between p-4 rounded-2xl border-2 transition-colors cursor-pointer ${notifPrefs.sms_enabled ? 'border-[#22D3EE] bg-[#ECFEFF]' : 'border-slate-200 bg-slate-50'}`}
              onClick={() => setNotifPrefs(p => ({ ...p, sms_enabled: !p.sms_enabled }))}
              data-testid="toggle-sms-notifications"
            >
              <div className="flex items-center gap-3">
                <MessageSquare className={`w-5 h-5 ${notifPrefs.sms_enabled ? 'text-[#0E7490]' : 'text-slate-400'}`} />
                <div>
                  <p className={`font-bold text-sm ${notifPrefs.sms_enabled ? 'text-[#0E7490]' : 'text-slate-500'}`}>SMS Notifications</p>
                  <p className="text-xs text-slate-400">Text messages for videos & reports</p>
                </div>
              </div>
              <div className={`w-12 h-7 rounded-full relative transition-colors ${notifPrefs.sms_enabled ? 'bg-[#22D3EE]' : 'bg-slate-300'}`}>
                <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${notifPrefs.sms_enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
            </div>
          </div>

          <Button
            data-testid="save-notification-prefs-btn"
            onClick={handleSaveNotifPrefs}
            disabled={savingPrefs}
            className="rounded-full bg-[#22D3EE] hover:bg-[#06B6D4] text-white font-bold gap-2"
          >
            <Save className="w-4 h-4" /> {savingPrefs ? 'Saving...' : 'Save Preferences'}
          </Button>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="rounded-3xl border-red-200 shadow-sm mt-6" data-testid="danger-zone">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Nunito' }}>
            <AlertTriangle className="w-5 h-5" /> Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Permanently delete your account and all associated data. This action cannot be undone.
          </p>
          <Button
            data-testid="delete-account-btn"
            variant="outline"
            onClick={() => setShowDeleteConfirm(true)}
            className="rounded-full border-2 border-red-300 text-red-600 hover:bg-red-50 font-bold gap-2"
          >
            <Trash2 className="w-4 h-4" /> Delete My Account
          </Button>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="rounded-3xl max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600" style={{ fontFamily: 'Nunito' }}>
              <AlertTriangle className="w-5 h-5" /> Delete Account
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">
              This will permanently delete your account, remove your sessions, and unlink you from any children profiles. <strong>This cannot be undone.</strong>
            </p>
            <div>
              <Label className="font-semibold text-slate-600">Type DELETE to confirm</Label>
              <Input
                data-testid="delete-confirm-input"
                className="mt-1 rounded-xl"
                placeholder="DELETE"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }} data-testid="cancel-delete-account-btn">
                Cancel
              </Button>
              <Button
                data-testid="confirm-delete-account-btn"
                disabled={deleteConfirmText !== 'DELETE' || deleting}
                onClick={async () => {
                  setDeleting(true);
                  try {
                    await api.delete('/auth/account');
                    toast.success('Account deleted. Goodbye!');
                    logout();
                    navigate('/login');
                  } catch (err) {
                    toast.error(getErrorMessage(err, 'Failed to delete account'));
                  } finally { setDeleting(false); }
                }}
                className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold gap-2 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" /> {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
